/**
 * 시나리오별 "소요 분출용량을 어떻게 정할 것인가" 안내.
 *
 * 17개 중 계산식이 있는 건 13·14·15 뿐이다(계산기가 붙어 있다).
 * 나머지는 표준이 **공정 열·물질수지로 구하라**고만 하므로, 설계자가
 * 무엇을 보고 어떤 순서로 값을 정해야 하는지를 여기에 정리해 둔다.
 *
 * 출처: API Standard 521, 2020년 6월판
 *  - Table 1 (인쇄 17~18쪽) — 액체·증기별 유량 지침
 *  - 4.4.2 ~ 4.4.16 본문
 *
 * ⚠ 표준 문장을 그대로 옮기지 않는다. 무엇을 뜻하는지 우리말로 풀어 쓰고,
 *   조항 번호를 함께 남겨 원문을 찾아갈 수 있게 한다.
 */

export interface RateGuidance {
  readonly item: number;
  /** 근거 조항 */
  readonly clause: string;
  /** Table 1 이 정의하는 소요 유량 — 액체 배출 */
  readonly liquid: string | null;
  /** Table 1 이 정의하는 소요 유량 — 증기 배출 */
  readonly vapor: string | null;
  /** 값을 정하기 전에 확인해야 할 자료 */
  readonly documents: readonly string[];
  /** 구하는 순서 */
  readonly steps: readonly string[];
  /** 실무에서 자주 틀리는 지점 */
  readonly cautions: readonly string[];
}

/** 모든 시나리오에 공통으로 걸리는 원칙. */
export const COMMON_PRINCIPLES: readonly string[] = [
  "유량은 **정상 운전 조건이 아니라 배출 조건(배출 압력·온도)에서** 산정합니다. " +
    "배출압은 운전압보다 높으므로 이 차이를 반영하면 소요 유량이 눈에 띄게 줄어드는 " +
    "경우가 많습니다 (API 521 Table 1 각주 a, 4.4.2.4).",
  "과압원과 보호 대상 사이 배관의 마찰 압력손실도 반영할 수 있습니다 (4.4.2.4).",
  "검토 중인 고장의 원인이 아닌 제어밸브는 **정상 최소 유량 위치**에 있다고 봅니다. " +
    "계장이 유리하게 동작해 줄 것이라는 크레딧은 인정되지 않습니다 (4.4.8.2).",
  "여러 밸브가 동시에 영향을 받는 **공통원인 고장**(제어계통, 전원 등)이 없는지 " +
    "확인하십시오 (4.4.2.4).",
];

export const RATE_GUIDANCE: readonly RateGuidance[] = [
  {
    item: 1,
    clause: "4.4.2",
    liquid: "펌프로 밀어 넣을 수 있는 최대 액체 유입량",
    vapor: "유입되는 스팀·증기 전량 + 배출 조건에서 설비 안에서 발생하는 증기량",
    documents: [
      "P&ID — 어느 밸브가 닫히면 배출 경로가 막히는지",
      "펌프 성능곡선 — 최대 유량(체절점 부근 포함)",
      "열·물질수지 — 설비로 들어오는 스팀·증기량",
      "리보일러·히터 데이터시트 — 내부에서 발생하는 증기량",
    ],
    steps: [
      "차단으로 막히는 배출 경로를 P&ID 에서 특정합니다.",
      "그때 계속 들어오는 유입량을 모두 더합니다 — 펌프 토출, 스팀, 상류 공정.",
      "설비 안에서 추가로 발생하는 증기(리보일러 등)를 더합니다.",
      "합계를 배출 조건에서 다시 계산합니다.",
      "정변위 펌프·압축기나 배관 최대유입이면 아래 계산기를 쓸 수 있습니다 " +
        "(표준식이 아니라 일반 유체역학을 적용한 보조 계산입니다).",
    ],
    cautions: [
      "고장 원인과 무관하게 정상 운전 중이던 수동·원격 밸브는 그대로 열려 있다고 봅니다 (4.4.2.4).",
      "액체가 차오르다 넘치는 경우와 증기가 쌓이는 경우는 배출 유체가 다릅니다. 어느 쪽인지 먼저 정하십시오.",
    ],
  },
  {
    item: 2,
    clause: "4.4.3",
    liquid: null,
    vapor: "배출 조건에서 응축기로 들어가는 증기 전량",
    documents: [
      "컬럼 시뮬레이션 — 배출 압력에서 다시 수렴시킨 결과",
      "응축기 데이터시트 — 정상 duty",
      "공랭기 설계 자료 — 자연대류 능력(팬 고장 시)",
      "오버헤드 어큐뮬레이터 용적 — 서지 여유 시간",
    ],
    steps: [
      "전응축인지 부분응축인지 정합니다.",
      "전응축(4.4.3.2.2): 응축기 유입 증기 전량. 배출 조건의 **새 조성·온도**로 재계산합니다.",
      "부분응축(4.4.3.2.3): 배출 조건에서 유입 증기량 − 유출 증기량.",
      "공랭기 팬 고장(4.4.3.2.4): 자연대류로 정상 duty 의 20~30 % 를 인정하고, 남는 70~80 % 로 산정합니다.",
    ],
    cautions: [
      "냉각수가 끊긴 뒤 남은 냉각수의 효과는 보통 크레딧을 주지 않습니다 — 지속 시간이 짧고 배관 형상에 좌우됩니다 (4.4.3.2.1).",
      "오버헤드 어큐뮬레이터의 서지 여유는 보통 10분 미만입니다. 그보다 길어지면 환류까지 끊겨 조성·온도·증기량이 크게 달라집니다 (4.4.3.2.2).",
      "루버 폐쇄는 **냉각 전손실**로 봅니다 — 부분 크레딧을 주지 마십시오 (4.4.3.2.5).",
    ],
  },
  {
    item: 3,
    clause: "4.4.3",
    liquid: null,
    vapor:
      "유입 스팀·증기 전량 + 배출 조건 내부 발생량 − 측류 환류가 응축시키는 증기량",
    documents: [
      "컬럼 시뮬레이션 — 환류가 없는 조건",
      "리보일러 duty",
      "환류 펌프 데이터시트 — 정지 시 거동",
    ],
    steps: [
      "환류가 끊긴 상태로 컬럼을 다시 수렴시킵니다.",
      "탑상으로 올라오는 증기량에서 측류 환류가 응축시키는 몫을 뺍니다.",
      "배출 조건에서 다시 계산합니다.",
    ],
    cautions: [
      "환류 상실은 응축기를 넘치게 만들어 **냉각 전손실과 같은 결과**가 되는 경우가 많습니다. 냉각 전손실로 사이징한 밸브면 대개 충분하지만, 계통별로 확인해야 합니다 (4.4.3.2.6).",
      "환류가 끊기면 조성이 달라져 증기 물성(분자량·비열비)이 바뀝니다. 기존 물성을 그대로 쓰지 마십시오.",
    ],
  },
  {
    item: 4,
    clause: "4.4.3",
    liquid: null,
    vapor: "배출 조건에서 설비로 들어가는 증기량 − 나가는 증기량의 차",
    documents: [
      "펌프어라운드 회로의 정상 제거 열량 (열·물질수지)",
      "배출 조건에서의 증발잠열",
      "컬럼 시뮬레이션",
    ],
    steps: [
      "펌프어라운드가 정상적으로 제거하던 열량을 확인합니다.",
      "그 열량이 그대로 증발에 쓰인다고 보고 증발량을 구합니다 (4.4.3.2.7).",
      "잠열은 **배출 지점의 배출 조건** 온도·압력 기준으로 잡습니다.",
    ],
    cautions: [
      "잠열을 정상 운전 조건에서 잡으면 유량이 과소평가됩니다 (4.4.3.2.7).",
    ],
  },
  {
    item: 5,
    clause: "4.4.4",
    liquid: null,
    vapor: "통상 없음 (None, normally)",
    documents: ["흡수탑 물질수지", "린오일 순환 계통 P&ID"],
    steps: [
      "린오일이 끊겼을 때 흡수탑 자체에 과압이 생기는지 확인합니다 — 표준은 통상 생기지 않는다고 봅니다.",
      "과압이 없다고 판단되면 '검토 포함'을 끄고, 그 판단 근거를 상황 설명에 남기십시오.",
    ],
    cautions: [
      "흡수탑 자체는 괜찮아도, 흡수되던 성분이 그대로 하류로 넘어가 **다른 설비**에 과압을 만들 수 있습니다. 그쪽은 별도 시나리오로 검토하십시오.",
    ],
  },
  {
    item: 6,
    clause: "4.4.5",
    liquid: null,
    vapor: "탑에서는 항목 2와 같은 효과, 그 밖의 용기에서는 항목 1과 같은 효과",
    documents: [
      "항목 2 또는 항목 1 에서 쓰는 자료를 그대로 씁니다",
      "비응축가스 유입 경로 — 원료 조성, 벤트 계통 P&ID",
    ],
    steps: [
      "보호 대상이 탑이면 항목 2(응축기 냉각 상실)와 같은 방법으로 구합니다.",
      "그 밖의 용기면 항목 1(출구 차단)과 같은 방법으로 구합니다.",
    ],
    cautions: [
      "비응축가스가 쌓이면 분압이 올라가 응축이 막힙니다. 결과적으로 냉각 상실과 같은 상태가 됩니다.",
    ],
  },
  {
    item: 7,
    clause: "4.4.6",
    liquid: null,
    vapor:
      "⚠ 안전밸브로 대응하지 않습니다 — 대체 보호수단으로 상황 자체를 회피합니다",
    documents: [
      "스팀아웃·수압시험 후 잔류수 배출 절차",
      "열교환기 튜브 누설 경로 (→ 항목 15로 검토)",
      "SLT(취성파괴 한계온도) 검토 자료",
    ],
    steps: [
      "뜨거운 오일에 물이 들어갈 경로(4.4.6.1), 경질 탄화수소가 들어갈 경로(4.4.6.2)를 모두 찾습니다.",
      "그 경로를 설계·운전 절차로 막습니다 — 배수 확인, 인터록, 절차서.",
      "열교환기 튜브 파열이 원인이면 **항목 15**로 검토하십시오.",
    ],
    cautions: [
      "물이 뜨거운 오일에 닿으면 순간적으로 1,000배 넘게 팽창합니다. 어떤 안전밸브도 감당하지 못합니다.",
      "이 시나리오를 유량으로 잡아 밸브를 키우는 것은 표준의 의도가 아닙니다.",
    ],
  },
  {
    item: 8,
    clause: "4.4.7",
    liquid: "펌프로 밀어 넣을 수 있는 최대 액체 유입량",
    vapor: null,
    documents: [
      "펌프 성능곡선 — 최대 유량",
      "충전 라인 제어밸브 데이터시트 — 완전 개방 시 Cv 와 통과 유량",
      "레벨 계장 구성 — 고레벨 인터록 유무",
    ],
    steps: [
      "설비로 액체를 밀어 넣는 모든 경로를 찾습니다.",
      "각 경로에서 배출 조건 압력에 대항해 흐를 수 있는 최대 유량을 구합니다.",
      "합계를 소요 분출용량으로 씁니다.",
      "정변위 펌프나 배관 최대유입이면 아래 계산기를 쓸 수 있습니다 " +
        "(표준식이 아니라 일반 유체역학을 적용한 보조 계산입니다).",
    ],
    cautions: [
      "배출되는 것이 액체이므로 유체 상태를 **'액체'** 로 두고 L/min 으로 입력하십시오.",
      "넘친 액체가 배출 중 플래싱하면 2상 유동 사이징이 필요합니다.",
    ],
  },
  {
    item: 9,
    clause: "4.4.8",
    liquid: null,
    vapor: "케이스별로 해석 (Analyze on a case-by-case basis)",
    documents: [
      "제어밸브 데이터시트 — Cv, 고장 시 위치(FO/FC/FL)",
      "P&ID — 수동 바이패스 유무와 크기",
      "상·하류 압력 조건",
      "열·물질수지 — 정상 유출량과 유닛 턴다운",
    ],
    steps: [
      "입구 제어기기 고장(4.4.8.3): **입구 밸브 1개가 완전히 열린** 상태를 봅니다. 고장 시 위치와 무관하게 열린다고 가정합니다.",
      "나머지 입구 밸브는 정상 운전 위치를 유지한다고 봅니다.",
      "소요 유량 = 최대 예상 유입량 − 정상 유출량. 모두 배출 조건에서, 유닛 턴다운을 고려해 계산합니다.",
      "같은 고장으로 출구가 닫히거나 다른 입구가 열린다면 그것도 반영합니다.",
      "수동 바이패스가 열려 있을 가능성을 검토합니다 — 제어밸브 완전 개방 + 바이패스 정상 위치의 합계를 봅니다.",
      "제어밸브가 고장(완전 개방)나 배관 자체가 파단됐다고 볼 때는 아래 " +
        "계산기를 쓸 수 있습니다(표준식이 아니라 일반 유체역학을 적용한 " +
        "보조 계산입니다).",
    ],
    cautions: [
      "고장 대상이 아닌 제어밸브에는 **정상 최소 유량 위치**까지만 크레딧을 줍니다 (4.4.8.2).",
      "고압 용기의 액위가 사라져 고압 증기가 저압 계통으로 넘어가는 상황(가스 블로바이 / 증기 브레이크스루)을 반드시 확인하십시오. 저압 계통이 **제어밸브를 통과하는 증기 전량**을 받아야 할 수 있습니다 (4.4.8.3, Annex G).",
      "바이패스를 운전 절차로만 막는 경우, 절차가 지켜지지 않았을 때의 결과를 함께 평가해야 합니다 (4.2.1, 4.2.2).",
    ],
  },
  {
    item: 10,
    clause: "4.4.9",
    liquid: null,
    vapor: "과열로 생기는 비응축가스를 포함한 최대 증기 발생량 추정치",
    documents: [
      "리보일러·히터 최대 duty (제어 실패 시 도달 가능한 값)",
      "스팀 헤더 압력·유량 — 스팀 밸브 오개방 시",
      "체크밸브 위치와 상류 압력 — 역류 경로",
    ],
    steps: [
      "이상 열 유입(4.4.9.1): 제어가 실패했을 때 도달할 수 있는 최대 열량을 잡고 증발량을 구합니다.",
      "밸브 오개방(4.4.9.2): 열린 밸브를 통과하는 스팀·고온 유체량을 구합니다.",
      "체크밸브 고장(4.4.9.3): 역류로 들어오는 고압 유체량을 구합니다.",
      "밸브 오개방·체크밸브 고장이면 아래 계산기를 쓸 수 있습니다 " +
        "(표준식이 아니라 일반 유체역학을 적용한 보조 계산입니다).",
    ],
    cautions: [
      "과열되면 원래 없던 비응축가스가 생길 수 있습니다. 증기 물성이 달라지므로 분자량·비열비를 다시 잡으십시오.",
      "체크밸브 고장은 단독 고장으로 볼지 다른 고장과 겹칠지 사내 기준을 따르십시오.",
    ],
  },
  {
    item: 11,
    clause: "4.4.10",
    liquid: "⚠ 안전밸브로 대응하지 않습니다 — 상황 회피 설계로",
    vapor: "⚠ 안전밸브로 대응하지 않습니다 — 상황 회피 설계로",
    documents: [
      "배관 응력 해석",
      "서지(수격) 해석 결과",
      "밸브 개폐 시간, 운전 절차",
    ],
    steps: [
      "이 시나리오는 유량을 넣는 대상이 아닙니다. '검토 포함'을 끄십시오.",
      "대신 워터해머·스팀해머·응축수해머가 생기지 않도록 설계와 절차로 막습니다.",
    ],
    cautions: [
      "압력 서지는 밀리초 단위로 올라갑니다. 안전밸브가 열리는 속도보다 훨씬 빠릅니다.",
    ],
  },
  {
    item: 12,
    clause: "4.4.11",
    liquid: null,
    vapor: "정상 조건과 폭주 조건 모두에서의 기체·증기 발생량 추정치 (2상 효과 고려)",
    documents: [
      "반응 속도론 자료 — 온도에 따른 반응속도",
      "반응열 (발열량)",
      "단열 열량계 시험 — ARC, VSP 등",
      "DIERS 방법 자료 (2상 배출 판정)",
    ],
    steps: [
      "정상 반응 조건에서의 기체 발생량을 구합니다.",
      "폭주(runaway) 조건에서의 최대 발생량을 구합니다 — 보통 이쪽이 지배합니다.",
      "배출이 2상이 될지 판정합니다. 2상이면 유량과 함께 **2상 유동 사이징**으로 넘어가야 합니다.",
    ],
    cautions: [
      "표준이 명시적으로 **2상 효과를 고려하라**고 합니다. 기체만으로 사이징하면 크게 부족해집니다.",
      "발열 반응계는 이 앱의 단상 계산으로 끝내지 마십시오. DIERS 방법 결과를 유량으로 넣고, 사이징은 2상 유동 화면을 쓰십시오.",
    ],
  },
  {
    item: 13,
    clause: "4.4.12",
    liquid: "4.4.12 참조 — 이 앱의 계산기가 식 (2)로 구합니다",
    vapor: null,
    documents: [
      "체적팽창계수 αv — 공정 설계자료 (없으면 Table 2 에서 API 도로 선택)",
      "갇힌 액체의 비열",
      "유입 열량 — 열교환기는 운전 중 최대 duty, 배관은 태양복사",
    ],
    steps: [
      "아래 계산기를 여십시오 — 표준 식 (2)로 계산합니다.",
    ],
    cautions: [
      "결과가 아주 작게 나옵니다. 표준도 실무에서 DN 20 × DN 25 밸브를 관례로 쓴다고 안내합니다 (4.4.12.2).",
      "갇힌 액체가 배출압에서 기포점을 넘으면 증발이 일어나 훨씬 큰 밸브가 필요합니다 (4.4.13.2.5.3).",
    ],
  },
  {
    item: 14,
    clause: "4.4.13",
    liquid: null,
    vapor: "4.4.13.2 또는 Annex A 의 방법으로 추정 — 이 앱의 계산기가 식 (7)/(8)/(9)로 구합니다",
    documents: [
      "용기 치수와 화재 시 예상 액위",
      "배출 조건에서의 증발잠열",
      "보온 사양 — 화재 등급 여부와 열전도도",
      "배수·소방설비 현황",
      "용기 재질의 허용 최대 벽면 온도 (기체 케이스)",
    ],
    steps: ["아래 계산기를 여십시오 — 액체가 있는 용기와 기체만 있는 용기를 구분해 계산합니다."],
    cautions: [
      "보온이 없으면 환경계수를 적용하지 않습니다 (Table 5 각주 e). 계산기가 자동으로 막습니다.",
      "기체만 있는 용기는 표준이 **유량이 아니라 면적**을 줍니다.",
    ],
  },
  {
    item: 15,
    clause: "4.4.14",
    liquid: "튜브 1개 단면적의 2배를 지나는 액체 유량",
    vapor: "튜브 1개 단면적의 2배를 지나는 스팀·증기 유량",
    documents: [
      "열교환기 데이터시트 — 튜브 안지름, 재질",
      "고압측 운전 압력·온도·물성",
      "저압측 배관 용량 (공제 검토용)",
    ],
    steps: ["아래 계산기를 여십시오 — 개구부 2개소로 놓고 단상 유동식으로 계산합니다."],
    cautions: [
      "고압측 유체가 파단부에서 플래싱하면 단상 식을 쓸 수 없습니다. 이상유동(HEM)으로 구해 직접 입력하십시오.",
      "저압측 배관으로 빠지는 용량은 공제할 수 있습니다 (4.4.14.2.4). 이 앱은 아직 반영하지 않습니다.",
    ],
  },
  {
    item: 16,
    clause: "4.4.15",
    liquid: null,
    vapor:
      "동력 상실이 설비에 미치는 영향을 검토해, 일어날 수 있는 **가장 나쁜 조건**으로 사이징",
    documents: [
      "전력 계통도 — 어느 기기가 어느 모선에 물려 있는지",
      "UPS·비상발전 적용 범위",
      "각 기기(펌프·팬·교반기) 데이터시트",
    ],
    steps: [
      "정전 범위를 정합니다 — 부분 정전인지 전 공장 정전인지.",
      "분류탑(a): 모든 펌프가 정지해 **환류와 냉각수가 동시에** 끊긴다고 봅니다.",
      "반응기(b): 교반·급랭·억제제 주입이 끊깁니다. **폭주 반응의 증기 발생량**으로 사이징합니다.",
      "공랭기(c): 팬 정지. 정상 duty 와 비상시 duty 의 **차이**로 사이징합니다.",
      "서지 드럼(d): 최대 액체 유입량.",
    ],
    cautions: [
      "정전은 여러 시나리오를 **동시에** 일으킵니다. 개별 시나리오의 단순 합이 아니라, 동시 발생을 반영한 하나의 케이스로 잡아야 합니다.",
      "반응기는 정전이 곧 폭주로 이어질 수 있어 이 케이스가 지배하는 경우가 많습니다.",
    ],
  },
  {
    item: 17,
    clause: "4.4.16",
    liquid: null,
    vapor: "Table 1 에 유량 지침이 없습니다 — 어떤 원인인지에 따라 달라집니다",
    documents: [
      "정비 절차서 — 차단·퍼지·스팀아웃 순서",
      "맹판(블라인드) 설치 계획",
      "정비 중 격리되는 안전밸브 목록과 대체 벤트",
    ],
    steps: [
      "표준이 드는 정비 중 과압 원인 8가지를 확인합니다 — 고압 유체 유입, 잔류물과 반응하는 유체·에너지 유입, 잔류물을 증발시키는 고온 유체 유입, 열려 있어야 할 격리밸브가 닫힘, 예기치 않은 막힘, 스팀아웃, 퍼지, 외부 화재.",
      "해당하는 원인을 고르고, 그 원인에 맞는 시나리오(1·7·10·14 등)의 방법으로 유량을 구합니다.",
      "안전밸브가 격리되는데 대체 벤트가 없다면, 유량 계산이 아니라 **절차·인터록으로 막는 것**이 표준의 요구입니다.",
    ],
    cautions: [
      "표준은 정비 중 과압을 안전밸브·개방 벤트·기계적 인터록·운전 절차 중 하나로 반드시 막으라고 요구합니다 (shall).",
      "개방 벤트를 정비 중 배출 수단으로 쓴다면 그 벤트도 **용량이 충분한지** 확인해야 합니다.",
    ],
  },
];

export function findRateGuidance(item: number): RateGuidance | null {
  return RATE_GUIDANCE.find((g) => g.item === item) ?? null;
}
